import * as React from 'react';
import {
  Backdrop,
  Box,
  Button,
  Card,
  Dialog,
  DialogTitle,
  Fade,
  FormControl,
  InputLabel,
  ListItem,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridRenderCellParams,
  GridValueGetterParams,
  GridActionsCellItem,
  GridRowModes,
  GridColumns,
  GridRowModesModel,
  GridRowId,
  GridEventListener,
  GridRowParams,
  MuiEvent,
} from '@mui/x-data-grid';
import SaveIcon from '@mui/icons-material/Save';
import Add from '@mui/icons-material/Add';
import { Address } from 'src/careflow/ph-core/fhir-client/resources';
import { GridValueSetterParams } from '@mui/x-data-grid/models/params/gridCellParams';
import CancelIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { useState } from 'react';
import { DatePicker } from '@mui/lab';
import { isEmpty, isNil } from 'lodash';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import uuidv4 from 'src/utils/uuidv4';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { capitalize } from 'src/utils/string';
import { getExtensionArrayByURLs } from 'src/utils/fhir';
import { getVisibleAdministrativeFields } from 'src/utils/addressMapping';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { getLabelColor } from '../../referral/components/workflow-step/AddressChecklistItem/AddressList';
import AddAddressLoading from '../../referral/components/workflow-step/AddressChecklistItem/AddAddressLoading';

const pageSize = 3;

const stylemodal = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0 0 2px 0 rgb(145 158 171 / 20%), 0 12px 24px -4px rgb(145 158 171 / 12%)',
  p: 4,
};

export interface NewAddressDetailsProps {
  addressess: Address[];
  onAddressesSave: (data: Address[]) => Promise<void>;
  permissions?: {
    isAllowedToAdd: boolean;
    isAllowedToEdit: boolean;
    isAllowedToDelete: boolean;
    isAllowedAddressHousingStatus: boolean;
  };
  config: any,
  use?: string;
}

export function NewAddressDetails({
  addressess,
  onAddressesSave,
  permissions,
  config,
  use
}: NewAddressDetailsProps) {
  const { i18n } = useLocales();
  const { componentsData } = useTenantConfigData();
  const administrativeFields = getVisibleAdministrativeFields(componentsData?.patient?.address || {});

  const [ housingStatusVS, ] = useValueSet('udsplus-housing-status-codes', { map: ValueSetWrapper });

  const [disabledadd, setDisabledAdd] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [idDelete, setIdDelete] = React.useState('');
  const [rowDelete, setrowDelete] = React.useState<any>([]);
  const [editRow, setEditRow] = React.useState(null);
  const [disablePeriod, setDisablePeriod] = React.useState(true);
  const [rows, setRows] = React.useState<any>([]);
  const [openAddAddress, setOpenAddAddress] = React.useState(false);
  const [externalError, setExternalError] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [housingStatus, setHousingStatus] = useState<any>(null);
  const [error, setError] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  React.useEffect(() => {
    setRows([]);
    if (addressess !== undefined) {
      const getPatientAddress: Address[] = JSON.parse(JSON.stringify(addressess));
      getPatientAddress?.map((ident: any) => {
        if (!ident.id) ident.id = uuidv4();
      });
      setRows(getPatientAddress);
    }
  }, [addressess]);

  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const handleRowEditStart = (params: GridRowParams, event: MuiEvent<React.SyntheticEvent>) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleSaveClicks = (id: GridRowId) => () => {
    setDisabledAdd(false);
    setDisablePeriod(true);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows?.find((row: any) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows?.filter((row) => row.id !== id));
    }
    setDisabledAdd(false);
  };

  const validateDateFields = (start: any, end: any) => {
    let isValid = true;
    const startDate = moment(start ?? null);
    const endDate = moment(end ?? null);
    if (startDate.isValid() && endDate.isValid()) {
      if (startDate.isAfter(endDate)) {
        setError(true);
        isValid = false;
      }
    }

    if (
      config?.address?.requiredHousingStatus &&
      permissions?.isAllowedAddressHousingStatus &&
      isEmpty(housingStatus)
    ) {
      setExternalError(true);
      isValid = false;
    }

    return isValid;
  };

  const saveChanges = async (data: Address) => {
    if (!validateDateFields(startDate, endDate)) return;

    let period: any;

    if (!isNil(startDate) || !isNil(endDate)) {
      period = {
        ...(!isNil(startDate) && { start: new Date(startDate).toISOString() }),
        ...(!isNil(endDate) && { end: new Date(endDate).toISOString() }),
      };
    }

    const newRow: any = data;
    newRow.period = period;

    if(isEmpty(newRow?.extension)) {
      newRow.extension = [];
    }

    if(housingStatus){
      const housingStatusExtensionIndex = 
        newRow?.extension?.findIndex(item => item?.url === housingStatusVS?.getConceptSystem());
      const housingStatusExtension = {
        url: housingStatusVS?.getConceptSystem(),
        valueCode: housingStatus
      };
      if(housingStatusExtensionIndex !== undefined && housingStatusExtensionIndex !== -1){
        newRow.extension[housingStatusExtensionIndex] = housingStatusExtension;
      }else{
        newRow.extension.push(housingStatusExtension);
      }
    }

    const editRowId: any = editRow;

    const updateRow = rows?.map((obj) => {
      if (obj.id === editRowId?.id) return data;
      return obj;
    });

    const address = editRowId ? updateRow : [newRow, ...updateRow];

    try {
      onAddressesSave(address);
      setRows(address);
    } catch (error) {
      console.log('error', error);
    }
    handleCloseAddress();
  };

  const saveDeleteIdentifier = async () => {
    const updateAddress = rows?.filter((row: any) => row.id != idDelete);

    try {
      await onAddressesSave(updateAddress);

      setRows(updateAddress);
    } catch (error) {
      console.log('error', error);
    }
    handleClose();
  };

  const handleEditClicks = (id: GridRowId) => () => {
    setDisablePeriod(false);
    const editRow = rows?.find(({ id: rowId }: { id: any }) => rowId === id);
    if (editRow) {
      setEditRow(editRow);
      setStartDate(editRow?.period?.start);
      setEndDate(editRow?.period?.end);
      const housingStatusExtension = getExtensionArrayByURLs(
          editRow, 
          ["http://fhir.org/guides/hrsa/uds-plus/CodeSystem/udsplus-housing-status-codes"]
        )?.[0]?.valueCode 
        ?? config?.address?.defaultHousingStatus 
        ?? "";
      setHousingStatus(housingStatusExtension)
      setOpenAddAddress(true);
    }
  };

  const saveId = (id: any) => {
    setrowDelete(rows?.filter((row: any) => row.id === id));
    const typeModal = rows?.filter((row: any) => row.id === id)[0].id;
    setIdDelete(typeModal);
    handleOpen();
  };

  const aclEdit = !!permissions?.isAllowedToEdit;
  const aclDelete = !!permissions?.isAllowedToDelete;
  const showEdit = aclEdit || aclDelete;

  const countyConfig = config?.address?.county || { visible: true, label: 'County' };

  const columns: GridColumns = [
    {
      field: 'id',
      headerName: '',
      align: 'center',
      flex: 0.1,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={getLabelColor(params.row) === '#009764' ? 'Valid' : 'Invalid'}>
          <CheckCircleIcon htmlColor={getLabelColor(params.row, { noExtension: '#e2e2e9' })} />
        </Tooltip>
      ),
    },
    {
      field: 'street1',
      headerName: `${i18n('patients.details.addressDetails.streetOne', 'crs')}`,
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.line?.[0];
      },
      valueSetter: (params: GridValueSetterParams) => {
        return { ...params.row, line: [params.value] };
      },
      width: 220,
      editable: true,
      sortable: false,
    },
    {
      field: 'street2',
      headerName: `${i18n('patients.details.addressDetails.streetTwo', 'crs')}`,
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.line?.[1];
      },
      valueSetter: (params: GridValueSetterParams) => {
        const address = params.row as Address;
        const { value: newValue } = params;
        let addressLines = address.line;
        if (!addressLines || addressLines.length === 0) {
          addressLines = [newValue];
        } else if (addressLines.length === 1) {
          addressLines = [...addressLines, newValue];
        } else {
          addressLines[1] = newValue;
        }
        return { ...params.row, line: addressLines };
      },
      width: 150,
      editable: true,
      sortable: false,
    },
    {
      field: 'postalCode',
      headerName: `${i18n('patients.details.addressDetails.zipCode', 'crs')}`,
      width: 110,
      editable: true,
      sortable: false,
    },
    {
      field: 'city',
      headerName: `${i18n('patients.details.addressDetails.city', 'crs')}`,
      width: 160,
      editable: true,
      sortable: false,
    },
    // Dynamically add administrative fields columns
    ...administrativeFields.map(field => ({
      field: field.fhirField || field.name,
      headerName: field.label || field.name,
      width: 160,
      editable: true,
      sortable: false,
    })),
    {
      field: 'state',
      headerName: `${i18n('patients.details.addressDetails.state', 'crs')}`,
      width: 160,
      editable: true,
      sortable: false,
    },
    {
      field: 'country',
      headerName: `${i18n('patients.details.addressDetails.country', 'crs')}`,
      width: 160,
      editable: true,
      sortable: false,
    },
    {
      field: 'start',
      headerName: `${i18n('patients.details.addressDetails.start', 'crs')}`,
      editable: true,
      width: 160,
      type: 'date',
      renderCell: (params: GridRenderCellParams) => {
        const start = params.row.period !== undefined && params.row.period.start;
        const startDate = moment(start ? new Date(start) : null);
        return (
          <Typography variant="body2">
            {!startDate.isValid() ? '' : startDate.format('MM/DD/YYYY')}
          </Typography>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const start = params.row.period !== undefined ? new Date(params.row.period.start) : null;
        return start;
      },
      valueSetter: (params: GridValueSetterParams) => {
        const start = params.value ?? null;
        return { ...params.row, start };
      },
    },
    {
      field: 'end',
      headerName: `${i18n('patients.details.addressDetails.end', 'crs')}`,
      editable: true,
      width: 160,
      type: 'date',
      renderCell: (params: GridRenderCellParams) => {
        const end = params.row.period !== undefined && params.row.period.end;
        const endDate = moment(end ? new Date(end) : null);
        return (
          <Typography variant="body2">
            {!endDate.isValid() ? '' : endDate.format('MM/DD/YYYY')}
          </Typography>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const end = params.row.period !== undefined ? new Date(params.row.period.end) : null;
        return end;
      },
      valueSetter: (params: GridValueSetterParams) => {
        const end = params.value ?? null;
        return { ...params.row, end };
      },
    },
    {
      field: 'edit',
      headerName: '',
      type: 'actions',
      width: 90,
      cellClassName: 'actions',
      hide: !showEdit,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={'grid-a-save-' + id}
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClicks(id)}
            />,
            <GridActionsCellItem
              key={'grid-a-cancel-' + id}
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
            />,
          ];
        }

        return [
          aclEdit ? (
            <GridActionsCellItem
              label="Edit"
              className="textPrimary"
              onClick={handleEditClicks(id)}
              color="inherit"
              showInMenu
            />
          ) : (
            <></>
          ),
          aclDelete ? (
            <GridActionsCellItem
              label="Delete"
              className="textPrimary"
              onClick={() => {
                saveId(id);
              }}
              color="inherit"
              showInMenu
            />
          ) : (
            <></>
          ),
        ];
      },
    },
  ];

  const handleCloseAddress = () => {
    setOpenAddAddress(false);
    setEditRow(null);
    setStartDate(null);
    setEndDate(null);
    setHousingStatus(null);
    setExternalError(false);
    setError(false);
  };

  return (
    <div>
      <Stack direction="column" sx={{ padding: 3 }}>
        <Typography
          style={{ display: 'flex', justifyContent: 'space-between' }}
          variant="button"
          sx={{ textTransform: 'uppercase' }}
        >
          {!!permissions?.isAllowedToAdd && (
            <Button
              variant="contained"
              disabled={disabledadd}
              color="primary"
              startIcon={<Add />}
              onClick={() => setOpenAddAddress(true)}
              style={{ position: 'absolute', top: '20px', right: '25px' }}
            >
              {i18n('patients.details.addressDetails.button', 'crs')}
            </Button>
          )}
        </Typography>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={rows ? rows : []}
            columns={columns}
            editMode="row"
            pageSize={pageSize}
            rowModesModel={rowModesModel}
            autoHeight
            onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
            onRowEditStart={handleRowEditStart}
            onRowEditStop={handleRowEditStop}
            sx={{
              '& .MuiDataGrid-row': {
                borderBottom: '1px solid #EAECF0',
                '&:nth-of-type(odd)': {
                  backgroundColor: '#FAFAFA',
                },
                '&:hover': {
                  backgroundColor: '#F5F6F7',
                },
              },
            }}
          />
          <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
            }}
          >
            <Fade in={open}>
              <Box sx={stylemodal}>
                <Typography id="transition-modal-title" variant="h6" component="h2">
                  <div style={{ textAlign: 'center', fontSize: '25px', fontWeight: '300' }}>
                    Delete Address{' '}
                    <span style={{ fontWeight: '600' }}>{rowDelete?.[0]?.country}:</span>{' '}
                    {rowDelete?.[0]?.line[0]}, {rowDelete?.[0]?.state} {rowDelete?.[0]?.city}
                  </div>
                </Typography>
                <div style={{ textAlign: 'center', paddingTop: '30px' }}>
                  <Button variant="contained" onClick={saveDeleteIdentifier}>
                    Confirm
                  </Button>
                </div>
              </Box>
            </Fade>
          </Modal>
        </Box>
      </Stack>
      <Dialog open={openAddAddress} fullWidth maxWidth="md">
        <DialogTitle>
          {editRow !== null ?
            i18n('patients.details.addressDetails.titleEditAddress', 'crs'):
            i18n('patients.details.addressDetails.titleAddAddress', 'crs')
          } 
        </DialogTitle>
        <Card sx={{ p: 2, overflowY: 'auto' }}>
          <AddAddressLoading
            externalAddress={editRow}
            isEditable={true}
            handleClose={handleCloseAddress}
            handleSave={saveChanges}
            externalError={externalError}
            {...(permissions?.isAllowedAddressHousingStatus ? { 
              childrenTop: 
                <ListItem>
                  <FormControl fullWidth error={externalError}>
                    <InputLabel id="address-housing-status-label">
                      {i18n('patients.details.addressDetails.housingStatus', 'crs')}
                      {config?.address?.requiredHousingStatus ? "*" : ""}
                    </InputLabel>
                    <Select
                      labelId="address-housing-status-label"
                      id="addressHousingStatus"
                      name="addressHousingStatus"
                      label={`${i18n('patients.details.addressDetails.housingStatus', 'crs')}
                        ${config?.address?.requiredHousingStatus ? "*" : ""}`}
                      value={housingStatus ?? ""}
                      onChange={(ev) => {
                        setHousingStatus(String(ev.target.value));
                        setExternalError(false);
                      }}
                    >
                      {housingStatusVS?.asList().map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                          {capitalize(option?.code?.replaceAll("-", " "))}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </ListItem>
              } : {}
            )}
            childrenBottom={
              <Stack direction="row" spacing={1} sx={{ width: '100%', px: 2, pt: 1 }}>
                <DatePicker
                  label={i18n('patients.details.addressDetails.startDate', 'crs')}
                  value={startDate ?? null}
                  onChange={(value) => {
                    setStartDate(value);
                    setError(false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      variant="outlined"
                      {...params}
                      fullWidth
                      error={error}
                      helperText={error && `Start Date can't be greater than End Date`}
                    />
                  )}
                />
                <DatePicker
                  label={i18n('patients.details.addressDetails.endDate', 'crs')}
                  value={endDate ?? null}
                  onChange={(value) => {
                    setEndDate(value);
                    setError(false);
                  }}
                  renderInput={(params) => <TextField variant="outlined" {...params} fullWidth />}
                />
              </Stack>
            }
            use={use}
          />
        </Card>
      </Dialog>
    </div>
  );
}
